import { useState, useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown } from "lucide-react";
import { BUSINESS } from "@/data/content";

const NAV = [
  { to: "/", label: "Home" },
  {
    label: "Accommodations",
    children: [
      { to: "/properties", label: "Furnished Units" },
      { to: "/furnished-private-homes", label: "Furnished Private Homes" },
      { to: "/luxury-vacation-homes", label: "Vacation Homes" },
    ],
  },
  { to: "/cleaning-services", label: "Cleaning Services" },
  { to: "/owners", label: "Property Owners" },
];

const slug = (s) => s.toLowerCase().replace(/\s+/g, "-");

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled ? "bg-[#FAFAF9]/80 backdrop-blur-xl border-b border-[#E7E5E4]" : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-24">
          <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group" onClick={() => setOpen(false)}>
            <img src={BUSINESS.logo} alt="Masaya Hospitality logo" className="h-14 w-auto object-contain mix-blend-multiply" />
            <span className="hidden sm:flex flex-col leading-none">
              <span className="text-base font-semibold tracking-tight text-[#1C1917]">Masaya Hospitality</span>
              <span className="text-[0.6rem] tracking-[0.22em] uppercase text-[#8A9A86] font-medium">Furnished Stays · Austin, TX</span>
            </span>
          </Link>

          <nav className="hidden xl:flex items-center gap-6">
            {NAV.map((item) => (
              item.children ? (
                <div key={item.label} className="relative group">
                  <button
                    data-testid={`nav-${slug(item.label)}`}
                    className="text-sm tracking-tight text-[#44403C] hover:text-[#1C1917] transition-colors duration-300 inline-flex items-center gap-1"
                  >
                    {item.label}
                    <ChevronDown className="w-3.5 h-3.5 transition-transform duration-300 group-hover:rotate-180" strokeWidth={1.75} />
                  </button>
                  <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 group-focus-within:opacity-100 group-focus-within:visible transition-all duration-200">
                    <div className="min-w-[240px] rounded-xl bg-white border border-[#E7E5E4] shadow-xl p-2" data-testid="nav-accommodations-menu">
                      {item.children.map((c) => (
                        <NavLink
                          key={c.to}
                          to={c.to}
                          data-testid={`nav-${slug(c.label)}`}
                          className={({ isActive }) =>
                            `block rounded-lg px-3 py-2 text-sm transition-colors ${isActive ? "bg-[#F5F5F4] text-[#1C1917] font-medium" : "text-[#44403C] hover:bg-[#F5F5F4]"}`
                          }
                        >
                          {c.label}
                        </NavLink>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={`nav-${slug(item.label)}`}
                  className={({ isActive }) =>
                    `text-sm tracking-tight transition-colors duration-300 hover:text-[#1C1917] ${
                      isActive ? "text-[#1C1917] font-medium" : "text-[#44403C]"
                    }`
                  }
                  end={item.to === "/"}
                >
                  {item.label}
                </NavLink>
              )
            ))}
          </nav>

          <div className="hidden xl:flex items-center gap-4">
            <Link
              to="/book"
              data-testid="header-book-btn"
              className="rounded-full bg-[#1C1917] text-white px-6 py-2.5 text-sm font-medium hover:bg-[#44403C] transition-colors duration-300"
            >
              Book
            </Link>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            className="xl:hidden text-[#1C1917] p-2"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" strokeWidth={1.5} /> : <Menu className="w-6 h-6" strokeWidth={1.5} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.3 }}
            className="xl:hidden bg-[#FAFAF9] border-b border-[#E7E5E4]"
            data-testid="mobile-menu"
          >
            <nav className="px-6 py-6 flex flex-col gap-1">
              {NAV.map((item) => (
                item.children ? (
                  <div key={item.label} className="py-2 border-b border-[#F5F5F4]">
                    <p className="py-1 text-xs uppercase tracking-[0.2em] text-[#8A9A86] font-medium">{item.label}</p>
                    {item.children.map((c) => (
                      <NavLink
                        key={c.to}
                        to={c.to}
                        data-testid={`mobile-nav-${slug(c.label)}`}
                        onClick={() => setOpen(false)}
                        className={({ isActive }) =>
                          `block py-2.5 text-xl font-light tracking-tight ${isActive ? "text-[#8A9A86]" : "text-[#1C1917]"}`
                        }
                      >
                        {c.label}
                      </NavLink>
                    ))}
                  </div>
                ) : (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.to === "/"}
                    data-testid={`mobile-nav-${slug(item.label)}`}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      `py-3 text-2xl font-light tracking-tight border-b border-[#F5F5F4] ${
                        isActive ? "text-[#8A9A86]" : "text-[#1C1917]"
                      }`
                    }
                  >
                    {item.label}
                  </NavLink>
                )
              ))}
              <Link to="/book" data-testid="mobile-book-btn" onClick={() => setOpen(false)} className="mt-5 rounded-full bg-[#1C1917] text-white px-6 py-3 text-sm font-medium text-center">
                Book
              </Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
